import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from './create-store';

/**
 * In-progress answers to an `ask_questions` batch, held OUTSIDE the card that
 * renders them (FRONT-1685, root cause B).
 *
 * The card used to keep its selections in local `useState`. That made the
 * draft's lifetime equal to the React subtree's lifetime — and the chat
 * message list's subtree identity is not stable across a turn settling:
 * `use-agent-chat.ts` replaces the whole displayed list with the server's
 * authoritative history the moment a turn finishes, which swaps the
 * just-streamed message's synthetic id for its real one and can change the
 * list's length. Every key available at the message level is unstable under
 * one of those two moves — the message id changes on the swap, the position
 * changes on a length change — so the card's own key cannot protect it, and
 * three successive attempts to find a stable key each fixed one path and left
 * another (see `tests/regressions/`).
 *
 * The draft is therefore keyed by the thing that genuinely never changes: the
 * server-side question batch it belongs to. Whatever the list does to the
 * tree, a remounted card reads its own draft back. Focus and scroll position
 * are still lost on a remount — that churn is a separate defect, and this
 * deliberately does not depend on it being fixed.
 *
 * Deliberately NOT persisted. A draft is transient input whose useful life is
 * one turn; writing half-answered questions into localStorage would outlive
 * that for no benefit and put user answers at rest on disk. Losing a draft on
 * reload is expected behaviour, which is also why the map is simply bounded
 * rather than swept — see MAX_DRAFTS.
 */
export interface QuestionDraft {
  /** Selected option labels per question index. `OTHER` rides in here too. */
  selections: Record<number, string[]>;
  /** Free-text "Other" content per question index. */
  others: Record<number, string>;
}

/**
 * The value a card with no draft yet reads. A module-level constant, NOT a
 * fresh object per call: zustand compares a selector's result by identity, so
 * returning a new empty object on every render would re-render forever.
 */
export const EMPTY_DRAFT: QuestionDraft = Object.freeze({
  selections: {},
  others: {},
}) as QuestionDraft;

/**
 * Upper bound on retained drafts. A long chat session can accumulate one per
 * question batch; nothing here is recoverable state, so the oldest entry is
 * dropped rather than tracked for expiry. Insertion order is JS object key
 * order for string keys, so "oldest" is the first key.
 */
const MAX_DRAFTS = 50;

/** The stable identity a draft belongs to: one question batch in one session. */
export function draftKey(sessionId: string, batchId: number): string {
  return `${sessionId}:${batchId}`;
}

export interface QuestionDraftStore {
  drafts: Record<string, QuestionDraft>;
  /** Select (radio) or toggle (checkbox) one option on one question. */
  toggleOption: (key: string, qi: number, label: string, multi: boolean) => void;
  /** Replace the free-text "Other" value for one question. */
  setOther: (key: string, qi: number, value: string) => void;
  /** Forget a batch's draft — called once its answers are actually submitted. */
  clearDraft: (key: string) => void;
}

/** Bound the map, evicting oldest-first. Returns the map to store. */
function bounded(drafts: Record<string, QuestionDraft>): Record<string, QuestionDraft> {
  const keys = Object.keys(drafts);
  if (keys.length <= MAX_DRAFTS) return drafts;
  const next = { ...drafts };
  for (const key of keys.slice(0, keys.length - MAX_DRAFTS)) delete next[key];
  return next;
}

function update(
  drafts: Record<string, QuestionDraft>,
  key: string,
  change: (draft: QuestionDraft) => QuestionDraft,
): Record<string, QuestionDraft> {
  const current = drafts[key] ?? EMPTY_DRAFT;
  return bounded({ ...drafts, [key]: change(current) });
}

/**
 * The selection a question ends up with after one option is clicked. Lifted
 * out of the store action so the rule reads on its own: a checkbox adds or
 * removes, a radio replaces outright.
 */
function nextSelection(current: string[], label: string, multi: boolean): string[] {
  if (!multi) return [label];
  if (current.includes(label)) return current.filter((l) => l !== label);
  return [...current, label];
}

function createQuestionDraftStore(): StoreApi<QuestionDraftStore> {
  return createStore<QuestionDraftStore>('question-draft-store', (set) => ({
    drafts: {},
    toggleOption: (key, qi, label, multi) =>
      set((s) => ({
        drafts: update(s.drafts, key, (draft) => {
          const next = nextSelection(draft.selections[qi] ?? [], label, multi);
          return { ...draft, selections: { ...draft.selections, [qi]: next } };
        }),
      })),
    setOther: (key, qi, value) =>
      set((s) => ({
        drafts: update(s.drafts, key, (draft) => ({
          ...draft,
          others: { ...draft.others, [qi]: value },
        })),
      })),
    clearDraft: (key) =>
      set((s) => {
        if (!(key in s.drafts)) return s;
        const next = { ...s.drafts };
        delete next[key];
        return { drafts: next };
      }),
  }));
}

let store: StoreApi<QuestionDraftStore> | undefined;

export function getQuestionDraftStore(): StoreApi<QuestionDraftStore> {
  if (!store) store = createQuestionDraftStore();
  return store;
}

export function useQuestionDrafts<T>(selector: (s: QuestionDraftStore) => T): T {
  return useStore(getQuestionDraftStore(), selector);
}
